import React, { FC, useCallback, DispatchWithoutAction } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../../../../../theme';
import { ExareStatus, MoneyFragment } from '@websky/graphql';
import Money from '../../../../../../Money';
import { ExchangeRefundOrderRequestStatus } from '@entities/order';
import { REFUND_PATH } from '@entities/refund';
import { useHistory } from '@shared/routing';
import { Props as NotificationProps } from '../../../../../../BaseComponents/Notification';

export interface Props {
	className?: string;
	show: boolean;
	status: ExareStatus;
	passengersNames?: string;
	splittedOrderId?: string;
	refundPrice?: MoneyFragment;
	ns?: string;
	onGoToTicket?: DispatchWithoutAction;
}

const RefundOrderRequestStatus: FC<Props> = ({
	className,
	show,
	status,
	splittedOrderId,
	refundPrice: refundPriceProps,
	ns = 'Refund'
}) => {
	const { t } = useTranslation(ns);
	const { RefundOrderRequestStatus: css } = useTheme('Features').Refund;

	const history = useHistory();

	const goToSplittedBooking = useCallback(() => {
		if (!splittedOrderId) {
			return;
		}

		history.push(`/booking/${splittedOrderId}`);
	}, [history, splittedOrderId]);

	const notificationStatus: NotificationProps['status'] = (() => {
		switch (status) {
			case ExareStatus.InProcess:
			case ExareStatus.AwaitingPayment:
			case ExareStatus.NeedConfirm:
				return 'warning';

			case ExareStatus.Refused:
			case ExareStatus.Rejected:
			case ExareStatus.TimelimitExpired:
				return 'error';

			case ExareStatus.Finished:
				return 'done';

			default:
				return 'warning';
		}
	})();

	const notificationTitle: string = (() => {
		switch (status) {
			case ExareStatus.InProcess:
			case ExareStatus.AwaitingPayment:
			case ExareStatus.NeedConfirm:
			case ExareStatus.Rejected:
			case ExareStatus.Refused:
			case ExareStatus.TimelimitExpired:
				return t(`RequestForRefund.${status}`);

			case ExareStatus.Finished:
				return t('Reservation refund completed successfully');

			default:
				return '';
		}
	})();

	const notificationText: React.ReactNode = (() => {
		switch (status) {
			case ExareStatus.InProcess:
			case ExareStatus.AwaitingPayment:
			case ExareStatus.NeedConfirm:
			case ExareStatus.Rejected:
			case ExareStatus.Refused:
			case ExareStatus.TimelimitExpired:
				return '';

			case ExareStatus.Finished:
				return (
					<>
						{t('Total amount')}:
						<Money
							money={{ currency: refundPriceProps?.currency, amount: Math.abs(refundPriceProps?.amount) }}
						/>
					</>
				);

			default:
				return '';
		}
	})();

	const notificationAction: { actionText: string; onClick: () => void } | null = (() => {
		switch (status) {
			case ExareStatus.InProcess:
			case ExareStatus.AwaitingPayment:
			case ExareStatus.NeedConfirm:
			case ExareStatus.Rejected:
			case ExareStatus.TimelimitExpired:
				return {
					actionText: t('Request'),
					onClick: () => history.push(`${REFUND_PATH.BASE}/${REFUND_PATH.REQUEST}`)
				};

			case ExareStatus.Finished:
				if (!splittedOrderId) {
					return {
						actionText: t('Request'),
						onClick: () => history.push(`${REFUND_PATH.BASE}/${REFUND_PATH.REQUEST}`)
					};
				}

				return {
					actionText: t('Open'),
					onClick: goToSplittedBooking
				};

			default:
				return null;
		}
	})();

	if (!show) {
		return null;
	}

	return (
		<div className={cn(css.wrapper, className)}>
			<ExchangeRefundOrderRequestStatus
				status={notificationStatus}
				title={notificationTitle}
				text={notificationText}
				actionText={notificationAction?.actionText}
				onClickAction={notificationAction?.onClick}
			/>
		</div>
	);
};

export default RefundOrderRequestStatus;
